import i18next from "i18next";

import type {
  KnownNotificationTopic,
  NotificationArea,
} from "@ergon-ai/utils/supabase";
import {
  formatCurrency,
  formatDate,
  formatTime,
  getCurrencyScaleFactor,
} from "@ergon-ai/utils";

const I18N_ITEMS = "MobileApp.notifications.items";

/**
 * The one area whose rows arrive already in the reader's language: the Stripe
 * webhook handler writes subscription title and body in six locales from the
 * profile language. Those rows render their own copy untouched and have no
 * dictionary entry. Every other area arrives in English and resolves here.
 */
type BackendLocalizedArea = Extract<NotificationArea, "subscription">;

/**
 * Topics whose copy this package owns: every known topic outside that area.
 *
 * `Extract`, not a bare literal: drop or rename the area upstream and this
 * becomes `never`, which puts the six topics back into the exhaustive map
 * below and fails the build here rather than shipping raw tokens.
 */
type AppCopyNotificationTopic = Exclude<
  KnownNotificationTopic,
  `${BackendLocalizedArea}/${string}`
>;

/** App-copy topics → the `title`/`body` pair under their translation key. */
const TOPIC_TRANSLATION_KEYS: Record<AppCopyNotificationTopic, string> = {
  "chat/message": `${I18N_ITEMS}.chat.message`,
  "calendar/reschedule_proposed": `${I18N_ITEMS}.calendar.rescheduleProposed`,
  "calendar/appointment_updated": `${I18N_ITEMS}.calendar.appointmentUpdated`,
  "calendar/appointment_reminder_24h": `${I18N_ITEMS}.calendar.appointmentReminder24h`,
  "calendar/appointment_reminder_1h": `${I18N_ITEMS}.calendar.appointmentReminder1h`,
  "calendar/block_reminder_1h": `${I18N_ITEMS}.calendar.blockReminder1h`,
  "system/activity_purchased": `${I18N_ITEMS}.system.activityPurchased`,
  "system/activity_approved": `${I18N_ITEMS}.system.activityApproved`,
  "system/activity_scheduled": `${I18N_ITEMS}.system.activityScheduled`,
  "system/activity_rejected": `${I18N_ITEMS}.system.activityRejected`,
  "system/activity_canceled": `${I18N_ITEMS}.system.activityCanceled`,
  "system/activity_completed": `${I18N_ITEMS}.system.activityCompleted`,
  "payment/payment_received": `${I18N_ITEMS}.payment.paymentReceived`,
  "payment/payment_failed": `${I18N_ITEMS}.payment.paymentFailed`,
  "payment/payout_paid": `${I18N_ITEMS}.payment.payoutPaid`,
  "payment/payout_failed": `${I18N_ITEMS}.payment.payoutFailed`,
  "payment/dispute": `${I18N_ITEMS}.payment.dispute`,
  "payment/account_status": `${I18N_ITEMS}.payment.accountStatus`,
  "payment/refund": `${I18N_ITEMS}.payment.refund`,
  "payment/subscription_refund": `${I18N_ITEMS}.payment.subscriptionRefund`,
};

/**
 * The topics this package writes copy for, under its own name for its
 * consumers, the same way `CHAT_SYSTEM_MESSAGE_TOKENS` re-exports
 * `KNOWN_ORDER_CHAT_TOKENS`.
 *
 * Read off the map above rather than filtered from `KNOWN_NOTIFICATION_TOPICS`:
 * the map is an exhaustive `Record` over `AppCopyNotificationTopic`, so its keys
 * ARE that set and the canonical list stays the source through the type. It
 * also keeps this module free of any RUNTIME import from
 * `@ergon-ai/utils/supabase` — the filtered version ran at module load, so
 * every suite that mocks that module without the topic list (eight do) crashed
 * on import, even ones that never render a notification.
 *
 * Do NOT redeclare the list. A topic added to the canonical list fails this
 * build until its copy exists, unless the backend already writes it localized.
 */
export const NOTIFICATION_TOPICS = Object.keys(
  TOPIC_TRANSLATION_KEYS,
) as readonly AppCopyNotificationTopic[];

/**
 * Which enriched variant each topic can reach.
 *
 * Only the reminder payload carries `startAt`/`serviceName` today. The other
 * members are written ahead of the backend on purpose, the same way
 * `SCHEDULE_TOKENS` carries `activity_scheduled`: the day a field starts
 * arriving the copy improves with no app change and no release.
 */
const SCHEDULE_TOPICS = new Set<KnownNotificationTopic>([
  "calendar/reschedule_proposed",
  "calendar/appointment_updated",
  "calendar/appointment_reminder_24h",
  "calendar/appointment_reminder_1h",
  "calendar/block_reminder_1h",
]);

/** A personal calendar block has no service, so it is deliberately absent. */
const SERVICE_TOPICS = new Set<KnownNotificationTopic>([
  "calendar/appointment_reminder_24h",
  "calendar/appointment_reminder_1h",
  "calendar/reschedule_proposed",
  "calendar/appointment_updated",
  "system/activity_purchased",
  "system/activity_approved",
  "system/activity_scheduled",
  "system/activity_rejected",
  "system/activity_canceled",
  "system/activity_completed",
]);

/**
 * Every topic an activity writes. The backend picks the side the reader is
 * not, so one neutral "with {name}" reads correctly to both apps.
 */
const COUNTERPARTY_TOPICS = new Set<KnownNotificationTopic>([
  "calendar/reschedule_proposed",
  "calendar/appointment_updated",
  "system/activity_purchased",
  "system/activity_approved",
  "system/activity_scheduled",
  "system/activity_rejected",
  "system/activity_canceled",
  "system/activity_completed",
]);

/**
 * Chat rows carry `senderId`, not a name, so this never fires today and every
 * chat notification reads the same. Written ahead anyway: one `senderName`
 * from the backend turns ten identical rows into ten useful ones.
 */
const SENDER_TOPICS = new Set<KnownNotificationTopic>(["chat/message"]);

/** `account_status` reports a Stripe state change, never a sum. */
const AMOUNT_TOPICS = new Set<KnownNotificationTopic>([
  "payment/payment_received",
  "payment/payment_failed",
  "payment/payout_paid",
  "payment/payout_failed",
  "payment/dispute",
  "payment/refund",
  "payment/subscription_refund",
]);

/**
 * Payments that name the person on the other end of the money, per side.
 * A buyer's refund copy has no person in it, so reading the name there would
 * pick a variant that does not exist and cost them the amount as well.
 */
const CUSTOMER_TOPICS: Record<
  NotificationAudience,
  ReadonlySet<KnownNotificationTopic>
> = {
  professional: new Set(["payment/payment_received", "payment/refund"]),
  consumer: new Set(),
};

/**
 * Topics a buyer reads differently from a seller. A refund is the one that
 * flips: the seller issued it, the buyer is getting their money back, and no
 * single sentence is honest to both.
 *
 * Everything absent here reads the same to either side, or only one side can
 * receive it at all.
 */
const CONSUMER_KEYS: Partial<Record<KnownNotificationTopic, string>> = {
  "payment/refund": `${I18N_ITEMS}.payment.refundReceived`,
  "payment/payment_received": `${I18N_ITEMS}.payment.paymentSent`,
  "payment/payment_failed": `${I18N_ITEMS}.payment.paymentFailedConsumer`,
  "payment/dispute": `${I18N_ITEMS}.payment.disputeRaised`,
  // A buyer has no payout and no Stripe account, so there is no buyer-side
  // sentence to write. The generic line is wrong about nothing.
  "payment/payout_paid": `${I18N_ITEMS}.fallback`,
  "payment/payout_failed": `${I18N_ITEMS}.fallback`,
  "payment/account_status": `${I18N_ITEMS}.fallback`,
};

/**
 * The floor under every path, in English, hardcoded.
 *
 * Reached only when i18next resolves nothing at all — uninitialised, or the
 * fallback keys missing from every locale. Without it that case renders the
 * raw key path, which is the exact thing this helper exists to prevent.
 */
const LAST_RESORT = {
  title: "New notification",
  body: "Open the app to see the details",
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * A translator the caller supplies: the resolved copy, or undefined when the
 * key is missing. Lets a web app on next-intl read the same dictionary.
 */
export type NotificationTranslate = (
  key: string,
  params?: Record<string, string>,
) => string | undefined;

/**
 * i18next returns the key itself when nothing resolves, so a bare `t()` turns
 * a missing translation into `MobileApp.notifications.items…` on screen.
 * Anything empty or key-shaped is treated as absent.
 */
const translateWithI18next: NotificationTranslate = (key, params) => {
  const value = i18next.t(key, params);
  return typeof value === "string" && value.trim() !== "" && value !== key
    ? value
    : undefined;
};

/** Row copy the backend froze in, unless it is blank or the topic echoed. */
const usableRowCopy = (
  value: string | null | undefined,
  topic: string,
): string | undefined => {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed !== "" && trimmed !== topic ? trimmed : undefined;
};

interface ScheduleParams {
  date: string;
  time: string;
}

const parseSchedule = (
  payload: Record<string, unknown> | undefined,
  locale: string,
): ScheduleParams | undefined => {
  const startAt = payload?.startAt;
  if (typeof startAt !== "string") return undefined;

  const date = new Date(startAt);
  if (Number.isNaN(date.getTime())) return undefined;

  // The ticket verified `timezone`; the rest of this repo writes `timeZone`.
  // The payload contract is `unknown` in @ergon-ai/utils, so neither spelling
  // is pinned anywhere we can check — accept both rather than silently drop
  // the zone and render every reminder in device time with no error.
  const zone = payload?.timezone ?? payload?.timeZone;
  const timeZone =
    typeof zone === "string" && zone.trim() !== "" ? zone : undefined;

  // An IANA name the platform does not know makes Intl throw. A reminder is
  // worth more with the wrong zone than not at all, so fall back to the
  // device zone rather than dropping the date.
  try {
    return {
      date: formatDate(date, locale, timeZone),
      time: formatTime(date, locale, timeZone),
    };
  } catch {
    return { date: formatDate(date, locale), time: formatTime(date, locale) };
  }
};

/** A blank string is the same as no value: it would render a dangling label. */
const parseText = (
  payload: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() !== ""
    ? value.trim()
    : undefined;
};

/** Amounts arrive in minor units, like every other price the apps format. */
const parseAmount = (
  payload: Record<string, unknown> | undefined,
  locale: string,
): string | undefined => {
  const value = payload?.amount;
  const currency = payload?.currency;
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    typeof currency !== "string" ||
    currency.trim() === ""
  ) {
    return undefined;
  }

  return formatCurrency(
    {
      value,
      scaleFactor: getCurrencyScaleFactor(currency, locale),
      currency,
    },
    locale,
  );
};

export interface NotificationText {
  title: string;
  body: string;
}

/** Which side of the marketplace is reading. Each app knows its own. */
export type NotificationAudience = "professional" | "consumer";

export interface GetNotificationTextOptions {
  /** The row's `topic`, `area/name` (e.g. `payment/payment_received`). */
  topic: string;
  /** The row's payload JSON. Accepted as `unknown` and validated field by field. */
  payload?: unknown;
  /** The row's own title, which the backend writes in English. */
  title?: string | null;
  /** The row's own body, same provenance as `title`. */
  body?: string | null;
  /**
   * Defaults to `professional` because every topic that reads differently by
   * side is currently sent to sellers only.
   */
  audience?: NotificationAudience;
  /**
   * Dictionary lookup. Defaults to the global i18next the native apps run;
   * the web passes next-intl's, so both read one dictionary.
   */
  translate?: NotificationTranslate;
  /** Formats dates and amounts. Defaults to i18next's current language. */
  locale?: string;
}

/**
 * Localized title and body for a notification row.
 *
 * The backend freezes copy at write time and writes activity rows in English
 * with the language argument hardcoded, so the only way six locales read
 * their own language is for the app to own the strings. Known topics resolve
 * from the dictionary; the row's frozen copy is the fallback, not the source.
 *
 * An unknown topic NEVER renders the token or an empty string. It degrades to
 * the row's own English copy, then to generic localized copy, then to
 * `LAST_RESORT`. A new backend topic therefore reads as something sensible on
 * an app version that predates it. A `subscription/*` row takes the same
 * first step on purpose: its row copy is already in the reader's language.
 */
export const getNotificationText = ({
  topic,
  payload,
  title,
  body,
  audience = "professional",
  translate = translateWithI18next,
  locale = i18next.language,
}: GetNotificationTextOptions): NotificationText => {
  const resolve = translate;
  const fallback = (): NotificationText => {
    const rowTitle = usableRowCopy(title, topic);
    const rowBody = usableRowCopy(body, topic);

    // One language per row. Row copy is English (frozen by the backend), the
    // generic copy is the reader's language, and resolving the halves
    // independently pairs them: an English title over an Italian body. So any
    // row copy completes from LAST_RESORT, keeping a specific English title
    // rather than swapping it for a localized one that says nothing.
    if (rowTitle || rowBody) {
      return {
        title: rowTitle ?? LAST_RESORT.title,
        body: rowBody ?? LAST_RESORT.body,
      };
    }

    return {
      title: resolve(`${I18N_ITEMS}.fallback.title`) ?? LAST_RESORT.title,
      body: resolve(`${I18N_ITEMS}.fallback.body`) ?? LAST_RESORT.body,
    };
  };

  // Own-property guard: `topic` is backend-supplied, and a bare index would
  // resolve Object.prototype members — a topic of "toString" must never hand
  // a function to i18next.
  if (!Object.hasOwn(TOPIC_TRANSLATION_KEYS, topic)) return fallback();

  const known = topic as AppCopyNotificationTopic;
  const key =
    (audience === "consumer" ? CONSUMER_KEYS[known] : undefined) ??
    TOPIC_TRANSLATION_KEYS[known];
  const meta = isRecord(payload) ? payload : undefined;

  const schedule = SCHEDULE_TOPICS.has(known)
    ? parseSchedule(meta, locale)
    : undefined;
  const serviceName = SERVICE_TOPICS.has(known)
    ? parseText(meta, "serviceName")
    : undefined;
  const amount = AMOUNT_TOPICS.has(known)
    ? parseAmount(meta, locale)
    : undefined;
  const senderName = SENDER_TOPICS.has(known)
    ? parseText(meta, "senderName")
    : undefined;
  const customerName = CUSTOMER_TOPICS[audience].has(known)
    ? parseText(meta, "customerName")
    : undefined;
  const counterpartyName = COUNTERPARTY_TOPICS.has(known)
    ? parseText(meta, "counterpartyName")
    : undefined;

  const params: Record<string, string> = {};
  if (schedule) {
    params.date = schedule.date;
    params.time = schedule.time;
  }
  if (serviceName) params.serviceName = serviceName;
  if (amount) params.amount = amount;
  if (senderName) params.senderName = senderName;
  if (customerName) params.customerName = customerName;
  if (counterpartyName) params.counterpartyName = counterpartyName;

  // Richest match first. A suffix with no key falls back to the plain body
  // below, so an unauthored combination degrades instead of breaking.
  let suffix = "";
  if (amount && customerName) suffix = "_withAmountCustomer";
  else if (serviceName && counterpartyName) suffix = "_withServiceCounterparty";
  else if (schedule && serviceName) suffix = "_withScheduleService";
  else if (schedule) suffix = "_withSchedule";
  else if (serviceName) suffix = "_withService";
  else if (amount) suffix = "_withAmount";
  else if (senderName) suffix = "_withSender";

  const resolvedTitle = resolve(`${key}.title`);
  const resolvedBody =
    resolve(`${key}.body${suffix}`, params) ?? resolve(`${key}.body`);

  // A half-resolved pair means the dictionary lost a key. Degrade the whole
  // row together rather than pairing real copy with a raw key path.
  return resolvedTitle && resolvedBody
    ? { title: resolvedTitle, body: resolvedBody }
    : fallback();
};
